'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { EnterHomeCreationType } from '@enter/business-components/lib/pending-enter-home-prompt';
import { useMixpanel } from '@/contexts/mixpanel';
import { createEnterHomeAuthAttempt, type EnterAuthEntry } from '@/contexts/auth/login-analytics';

export const LANDING_V2_SCREEN_IDS = [
    'hero',
    'build_gallery',
    'easy_build',
    'easy_scale',
    'anything',
    'business',
    'feedback',
    'faq',
    'final_cta',
    'footer',
] as const;

export type LandingV2ScreenId = (typeof LANDING_V2_SCREEN_IDS)[number];
export type LandingV2UiVariant = 'desktop' | 'mobile';
export type LandingV2SubmitMethod = 'button' | 'keyboard_enter';
export type LandingV2ActionType = 'login' | 'navigate' | 'open_modal' | 'scroll' | 'select' | 'submit' | 'toggle';

interface LandingButtonTrackingInput {
    buttonId: string;
    screenId: LandingV2ScreenId;
    actionType: LandingV2ActionType;
    destination?: string;
    contentKey?: string;
    creationType?: EnterHomeCreationType;
    triggersLogin?: boolean;
}

interface LandingPromptSubmitTrackingInput {
    creationType: EnterHomeCreationType;
    submitMethod: LandingV2SubmitMethod;
    promptLength: number;
    journeyId: string;
}

export type LandingButtonTrackingAttributes = {
    'data-landing-button-id': string;
    'data-landing-button-screen': LandingV2ScreenId;
    'data-landing-action': LandingV2ActionType;
    'data-landing-destination'?: string;
    'data-landing-content-key'?: string;
    'data-landing-creation-type'?: EnterHomeCreationType;
    'data-landing-triggers-login'?: 'true';
};

interface LandingSessionState {
    sessionId: string;
    lastActivityAt: number;
}

interface LandingSessionResolution extends LandingSessionState {
    isNew: boolean;
}

interface LandingExposureState {
    sessionId: string;
    landingExposed: boolean;
    screenIds: LandingV2ScreenId[];
}

interface LandingScreenExposureGate {
    reset: () => void;
    update: (screenId: LandingV2ScreenId, visible: boolean, now: number) => boolean;
    nextDelay: (now: number) => number | null;
}

interface LandingV2TrackingContextValue {
    isLandingV2: boolean;
    getNavigationTrackingProps: () => Record<string, unknown> | null;
    trackButton: (input: LandingButtonTrackingInput) => void;
    trackPromptSubmit: (input: LandingPromptSubmitTrackingInput) => void;
}

const LANDING_SESSION_ID_KEY = 'enter-home:landing-v2:session-id';
const LANDING_SESSION_ACTIVITY_KEY = 'enter-home:landing-v2:last-activity-at';
const LANDING_EXPOSURE_STATE_KEY = 'enter-home:landing-v2:exposure-state';
export const LANDING_SESSION_IDLE_MS = 30 * 60 * 1000;
export const LANDING_SCREEN_EXPOSURE_MS = 500;
const LANDING_ACTIVITY_PERSIST_INTERVAL_MS = 5000;
const MOBILE_BREAKPOINT_PX = 768;
const SCREEN_ID_SET = new Set<string>(LANDING_V2_SCREEN_IDS);

const LandingV2TrackingContext = createContext<LandingV2TrackingContextValue>({
    isLandingV2: false,
    getNavigationTrackingProps: () => null,
    trackButton: () => undefined,
    trackPromptSubmit: () => undefined,
});

export function createLandingJourneyId(): string {
    return crypto.randomUUID();
}

function getAuthEntry(buttonId: string): EnterAuthEntry {
    if (buttonId === 'header.sign_in') return 'home_header_signin';
    if (buttonId === 'header.sign_up') return 'home_header_signup';
    if (buttonId === 'header.signin_popup.cta') return 'home_signin_popup';
    return 'product_auth_gate';
}

export function getPromptLengthBucket(length: number): '1_20' | '21_50' | '51_100' | '101_200' | '201_plus' {
    if (length <= 20) return '1_20';
    if (length <= 50) return '21_50';
    if (length <= 100) return '51_100';
    if (length <= 200) return '101_200';
    return '201_plus';
}

export function resolveLandingSession(
    current: LandingSessionState | null,
    now: number,
    createSessionId: () => string,
): LandingSessionResolution {
    const isExpired =
        !current ||
        !current.sessionId ||
        !Number.isFinite(current.lastActivityAt) ||
        now - current.lastActivityAt >= LANDING_SESSION_IDLE_MS;

    return {
        sessionId: isExpired ? createSessionId() : current.sessionId,
        lastActivityAt: now,
        isNew: isExpired,
    };
}

export function isLandingScreenEffectivelyVisible(
    rect: Pick<DOMRect, 'top' | 'bottom' | 'height'>,
    viewportHeight: number,
): boolean {
    if (rect.height <= 0 || viewportHeight <= 0) return false;
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    const requiredHeight = Math.min(240, rect.height * 0.25);
    return visibleHeight >= requiredHeight;
}

export function createLandingScreenExposureGate(
    initialExposedScreenIds: Iterable<LandingV2ScreenId> = [],
): LandingScreenExposureGate {
    const visibleSince = new Map<LandingV2ScreenId, number>();
    const exposed = new Set<LandingV2ScreenId>(initialExposedScreenIds);

    return {
        reset() {
            visibleSince.clear();
            exposed.clear();
        },
        update(screenId, visible, now) {
            if (exposed.has(screenId)) return false;
            if (!visible) {
                visibleSince.delete(screenId);
                return false;
            }

            const startedAt = visibleSince.get(screenId);
            if (startedAt === undefined) {
                visibleSince.set(screenId, now);
                return false;
            }
            if (now - startedAt < LANDING_SCREEN_EXPOSURE_MS) return false;

            visibleSince.delete(screenId);
            exposed.add(screenId);
            return true;
        },
        nextDelay(now) {
            let nextDelay: number | null = null;
            visibleSince.forEach((startedAt, screenId) => {
                if (exposed.has(screenId)) return;
                const delay = Math.max(0, LANDING_SCREEN_EXPOSURE_MS - (now - startedAt));
                nextDelay = nextDelay === null ? delay : Math.min(nextDelay, delay);
            });
            return nextDelay;
        },
    };
}

export function getLandingButtonTrackingProps(input: LandingButtonTrackingInput): LandingButtonTrackingAttributes {
    return {
        'data-landing-button-id': input.buttonId,
        'data-landing-button-screen': input.screenId,
        'data-landing-action': input.actionType,
        ...(input.destination ? { 'data-landing-destination': input.destination } : {}),
        ...(input.contentKey ? { 'data-landing-content-key': input.contentKey } : {}),
        ...(input.creationType ? { 'data-landing-creation-type': input.creationType } : {}),
        ...(input.triggersLogin ? { 'data-landing-triggers-login': 'true' as const } : {}),
    };
}

function getUiVariant(): LandingV2UiVariant {
    return window.innerWidth <= MOBILE_BREAKPOINT_PX ? 'mobile' : 'desktop';
}

function readStoredSession(): LandingSessionState | null {
    try {
        const sessionId = sessionStorage.getItem(LANDING_SESSION_ID_KEY);
        const rawLastActivityAt = sessionStorage.getItem(LANDING_SESSION_ACTIVITY_KEY);
        if (!sessionId || !rawLastActivityAt) return null;
        return {
            sessionId,
            lastActivityAt: Number(rawLastActivityAt),
        };
    } catch {
        return null;
    }
}

function persistSession(session: LandingSessionState): void {
    try {
        sessionStorage.setItem(LANDING_SESSION_ID_KEY, session.sessionId);
        sessionStorage.setItem(LANDING_SESSION_ACTIVITY_KEY, String(session.lastActivityAt));
    } catch {
        // Storage availability must not block the page or analytics delivery.
    }
}

function readStoredExposureState(sessionId: string): LandingExposureState | null {
    try {
        const rawState = sessionStorage.getItem(LANDING_EXPOSURE_STATE_KEY);
        if (!rawState) return null;
        const parsed = JSON.parse(rawState) as Partial<LandingExposureState>;
        if (parsed.sessionId !== sessionId || typeof parsed.landingExposed !== 'boolean') {
            return null;
        }

        const screenIds = Array.isArray(parsed.screenIds)
            ? Array.from(
                  new Set(
                      parsed.screenIds.filter(
                          (screenId): screenId is LandingV2ScreenId =>
                              typeof screenId === 'string' && SCREEN_ID_SET.has(screenId),
                      ),
                  ),
              )
            : [];

        return {
            sessionId,
            landingExposed: parsed.landingExposed,
            screenIds,
        };
    } catch {
        return null;
    }
}

function persistExposureState(state: LandingExposureState): void {
    try {
        sessionStorage.setItem(LANDING_EXPOSURE_STATE_KEY, JSON.stringify(state));
    } catch {
        // Storage availability must not block analytics delivery.
    }
}

function parseButtonTrackingInput(element: HTMLElement): LandingButtonTrackingInput | null {
    const buttonId = element.dataset.landingButtonId;
    const screenId = element.dataset.landingButtonScreen;
    const actionType = element.dataset.landingAction;
    if (!buttonId || !screenId || !SCREEN_ID_SET.has(screenId) || !actionType) {
        return null;
    }

    return {
        buttonId,
        screenId: screenId as LandingV2ScreenId,
        actionType: actionType as LandingV2ActionType,
        ...(element.dataset.landingDestination ? { destination: element.dataset.landingDestination } : {}),
        ...(element.dataset.landingContentKey ? { contentKey: element.dataset.landingContentKey } : {}),
        ...(element.dataset.landingCreationType === 'app' || element.dataset.landingCreationType === 'agent'
            ? {
                  creationType: element.dataset.landingCreationType as EnterHomeCreationType,
              }
            : {}),
        ...(element.dataset.landingTriggersLogin === 'true' ? { triggersLogin: true } : {}),
    };
}

export function LandingV2TrackingProvider({ children }: { children: ReactNode | ((tracking: LandingV2TrackingContextValue) => ReactNode) }) {
    const { enabled: analyticsEnabled, getDistinctId, logger } = useMixpanel();
    const sessionRef = useRef<LandingSessionState | null>(null);
    const lastPersistedActivityRef = useRef(0);
    const exposureStateRef = useRef<LandingExposureState | null>(null);
    const screenElementsRef = useRef(new Map<LandingV2ScreenId, Set<HTMLElement>>());
    const exposureGateRef = useRef(createLandingScreenExposureGate());
    const exposureTimerRef = useRef<number | null>(null);

    useEffect(() => {
        if (!analyticsEnabled) return;
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        logger(
            'auth_entry_expose',
            {
                entry: 'direct_link',
                load_state: navigation?.type === 'navigate' ? 'first_load' : 'navigation',
                duration_ms: Math.max(0, Math.round(performance.now())),
            },
            { onceKey: 'auth_entry_expose' },
        );
    }, [analyticsEnabled, logger]);

    const initializeExposureState = useCallback((sessionId: string, isNew: boolean) => {
        if (exposureStateRef.current?.sessionId === sessionId) return;

        const exposureState =
            (isNew ? null : readStoredExposureState(sessionId)) ?? {
                sessionId,
                landingExposed: false,
                screenIds: [],
            };
        exposureStateRef.current = exposureState;
        exposureGateRef.current = createLandingScreenExposureGate(exposureState.screenIds);
    }, []);

    const logLandingExpose = useCallback(
        (sessionId: string) => {
            const exposureState = exposureStateRef.current;
            if (exposureState?.sessionId === sessionId && exposureState.landingExposed) return;

            logger('home_landing_expose', {
                app: 'enter-home',
                surface: 'home_landing',
                page_type: 'landing',
                landing_session_id: sessionId,
                ui_variant: getUiVariant(),
            });

            const nextExposureState: LandingExposureState = {
                sessionId,
                landingExposed: true,
                screenIds: exposureState?.sessionId === sessionId ? exposureState.screenIds : [],
            };
            exposureStateRef.current = nextExposureState;
            persistExposureState(nextExposureState);
        },
        [logger],
    );

    const ensureSession = useCallback(
        (now = Date.now(), recordActivity = true): LandingSessionState => {
            const current = sessionRef.current ?? readStoredSession();
            if (
                !recordActivity &&
                current?.sessionId &&
                Number.isFinite(current.lastActivityAt)
            ) {
                sessionRef.current = current;
                return current;
            }
            const resolved = resolveLandingSession(current, now, () => `landing_${crypto.randomUUID()}`);

            if (resolved.isNew) {
                exposureGateRef.current.reset();
            }
            initializeExposureState(resolved.sessionId, resolved.isNew);

            sessionRef.current = {
                sessionId: resolved.sessionId,
                lastActivityAt: resolved.lastActivityAt,
            };
            if (resolved.isNew || now - lastPersistedActivityRef.current >= LANDING_ACTIVITY_PERSIST_INTERVAL_MS) {
                persistSession(sessionRef.current);
                lastPersistedActivityRef.current = now;
            }
            logLandingExpose(resolved.sessionId);
            return sessionRef.current;
        },
        [initializeExposureState, logLandingExpose],
    );

    const getCommonProps = useCallback((recordActivity = true) => {
        const session = ensureSession(Date.now(), recordActivity);
        return {
            app: 'enter-home',
            surface: 'home_landing',
            page_type: 'landing',
            landing_session_id: session.sessionId,
            ui_variant: getUiVariant(),
        };
    }, [ensureSession]);

    const trackButton = useCallback(
        (input: LandingButtonTrackingInput) => {
            if (!analyticsEnabled) return;
            const attempt = input.triggersLogin
                ? createEnterHomeAuthAttempt({
                      entry: getAuthEntry(input.buttonId),
                      loginIntent:
                          input.buttonId === 'header.sign_up' ||
                          input.buttonId === 'header.signin_popup.cta'
                              ? 'signup'
                              : 'login',
                      mixpanelDistinctId: getDistinctId(),
                  })
                : null;
            logger('home_landing_button_click', {
                ...getCommonProps(),
                button_id: input.buttonId,
                screen_id: input.screenId,
                action_type: input.actionType,
                ...(input.destination ? { destination: input.destination } : {}),
                ...(input.contentKey ? { content_key: input.contentKey } : {}),
                ...(input.creationType ? { creation_type: input.creationType } : {}),
                ...(input.triggersLogin ? { triggers_login: true } : {}),
                ...(attempt
                    ? {
                          entry: attempt.entry,
                          login_intent: attempt.loginIntent,
                          auth_trace_id: attempt.authTraceId,
                      }
                    : {}),
            });
        },
        [analyticsEnabled, getCommonProps, getDistinctId, logger],
    );

    const getNavigationTrackingProps = useCallback(
        () => analyticsEnabled ? getCommonProps() : null,
        [analyticsEnabled, getCommonProps],
    );

    const trackPromptSubmit = useCallback(
        (input: LandingPromptSubmitTrackingInput) => {
            if (!analyticsEnabled) return;
            logger('home_landing_prompt_submit', {
                ...getCommonProps(),
                screen_id: 'hero',
                creation_type: input.creationType,
                submit_method: input.submitMethod,
                prompt_length_bucket: getPromptLengthBucket(input.promptLength),
                journey_id: input.journeyId,
            });
        },
        [analyticsEnabled, getCommonProps, logger],
    );

    const evaluateScreens = useCallback(function evaluateCurrentScreens() {
        if (exposureTimerRef.current !== null) {
            window.clearTimeout(exposureTimerRef.current);
            exposureTimerRef.current = null;
        }
        if (!analyticsEnabled) return;

        const now = Date.now();
        const commonProps = getCommonProps(false);
        const pageVisible = document.visibilityState === 'visible';

        for (const screenId of LANDING_V2_SCREEN_IDS) {
            const elements = screenElementsRef.current.get(screenId);
            const visible =
                pageVisible &&
                !!elements &&
                Array.from(elements).some((element) =>
                    isLandingScreenEffectivelyVisible(element.getBoundingClientRect(), window.innerHeight),
                );

            if (exposureGateRef.current.update(screenId, visible, now)) {
                logger('home_landing_screen_expose', {
                    ...commonProps,
                    screen_id: screenId,
                });
                const exposureState = exposureStateRef.current;
                if (exposureState?.sessionId === commonProps.landing_session_id) {
                    const nextExposureState: LandingExposureState = {
                        ...exposureState,
                        screenIds: [...exposureState.screenIds, screenId],
                    };
                    exposureStateRef.current = nextExposureState;
                    persistExposureState(nextExposureState);
                }
            }
        }

        const nextDelay = exposureGateRef.current.nextDelay(now);
        if (nextDelay !== null) {
            exposureTimerRef.current = window.setTimeout(evaluateCurrentScreens, Math.max(1, nextDelay));
        }
    }, [analyticsEnabled, getCommonProps, logger]);

    useEffect(() => {
        if (!analyticsEnabled) return;
        ensureSession();

        const refreshScreenElements = () => {
            const nextElements = new Map<LandingV2ScreenId, Set<HTMLElement>>();
            document.querySelectorAll<HTMLElement>('[data-landing-screen]').forEach((element) => {
                const screenId = element.dataset.landingScreen;
                if (!screenId || !SCREEN_ID_SET.has(screenId)) return;
                const typedScreenId = screenId as LandingV2ScreenId;
                const elements = nextElements.get(typedScreenId) ?? new Set<HTMLElement>();
                elements.add(element);
                nextElements.set(typedScreenId, elements);
            });
            screenElementsRef.current = nextElements;
            evaluateScreens();
        };

        let frameId: number | null = null;
        const scheduleEvaluation = () => {
            ensureSession();
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(() => {
                frameId = null;
                evaluateScreens();
            });
        };
        const handleActivity = scheduleEvaluation;
        const handleClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest('[data-landing-tracking-owner="component"]')) return;
            const trackingElement = target.closest<HTMLElement>('[data-landing-button-id]');
            if (!trackingElement || trackingElement.tagName === 'SELECT') return;
            const input = parseButtonTrackingInput(trackingElement);
            if (input) trackButton(input);
        };
        const handleChange = (event: Event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) return;
            if (target.closest('[data-landing-tracking-owner="component"]')) return;
            const trackingElement = target.closest<HTMLElement>('[data-landing-button-id]');
            const input = trackingElement ? parseButtonTrackingInput(trackingElement) : null;
            if (input && target instanceof HTMLSelectElement) {
                input.contentKey = target.value;
            }
            if (input) trackButton(input);
        };

        const mutationObserver = new MutationObserver(refreshScreenElements);
        mutationObserver.observe(document.body, { childList: true, subtree: true });
        refreshScreenElements();

        window.addEventListener('scroll', scheduleEvaluation, { passive: true });
        window.addEventListener('resize', scheduleEvaluation);
        window.addEventListener('pointerdown', handleActivity, { passive: true });
        window.addEventListener('keydown', handleActivity);
        document.addEventListener('visibilitychange', scheduleEvaluation);
        document.addEventListener('click', handleClick, true);
        document.addEventListener('change', handleChange, true);

        return () => {
            mutationObserver.disconnect();
            window.removeEventListener('scroll', scheduleEvaluation);
            window.removeEventListener('resize', scheduleEvaluation);
            window.removeEventListener('pointerdown', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            document.removeEventListener('visibilitychange', scheduleEvaluation);
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('change', handleChange, true);
            if (frameId !== null) window.cancelAnimationFrame(frameId);
            if (exposureTimerRef.current !== null) {
                window.clearTimeout(exposureTimerRef.current);
            }
        };
    }, [analyticsEnabled, ensureSession, evaluateScreens, trackButton]);

    const value = useMemo<LandingV2TrackingContextValue>(
        () => ({
            isLandingV2: true,
            getNavigationTrackingProps,
            trackButton,
            trackPromptSubmit,
        }),
        [getNavigationTrackingProps, trackButton, trackPromptSubmit],
    );

    return <LandingV2TrackingContext.Provider value={value}>{typeof children === 'function' ? children(value) : children}</LandingV2TrackingContext.Provider>;
}

export function useLandingV2Tracking(): LandingV2TrackingContextValue {
    return useContext(LandingV2TrackingContext);
}
